import { Organization } from '@app/shared/models/organizations';
import { RoleDetails } from '@app/shared/models/user/role-details';
import { EMAIL_REGEX, EMAIL_REGEX_HELP, PHONE_REGEX, PHONE_REGEX_HELP } from '@app/shared/util/validators';
import { Authority } from '@technocom/auth';
import { SPACE } from '@technocom/common/platform';
import { ArrayUtils, StringUtils } from '@technocom/common/util';
import { Entity, EntityCommand, EntityConfiguration, Schema } from '@technocom/data/core';

export class UserDetails extends Entity<UserDTO, number> {
  getName(): string {
    if (this.notNil('fullName')) {
      return this.get('fullName');
    }
    return [this.get('lastName'), this.get('firstName'), this.get('middleName')]
      .filter(StringUtils.hasText)
      .join(SPACE);
  }
  getAuthorities(): Authority[] {
    return ArrayUtils.of(this.get('roles')).map(Entity.asId);
  }
  getOrganization(): Organization {
    return this.get('organization');
  }

  static readonly options: EntityConfiguration = Entity.extendOptions({
    title: 'Пользователь',
    serverUrl: '/auth-controller/user',
  });

  static readonly schema: Schema = Entity.extend<UserDTO>({
    id: 'username',
    text: 'fullName',
    fields: {
      username: {
        type: String,
        title: 'Имя пользователя',
        serialization: {
          from: 'login',
        },
        options: {
          order: 0,
          hidden: {
            editor: false,
          },
          column: {
            hidden: false,
          },
        },
      },
      fullName: {
        type: String,
        title: 'ФИО',
        serialization: {
          from: 'name',
        },
        options: {
          order: 10,
          hidden: {
            editor: true,
          },
        },
      },
      lastName: {
        type: String,
        title: 'Фамилия',
        options: {
          order: 20,
          hidden: {
            table: true,
          },
        },
      },
      firstName: {
        type: String,
        title: 'Имя',
        options: {
          order: 30,
          hidden: {
            table: true,
          },
        },
      },
      middleName: {
        type: String,
        title: 'Отчество',
        options: {
          order: 40,
          hidden: {
            table: true,
          },
        },
      },
      email: {
        type: String,
        title: 'Электронная почта',
        validation: {
          regex: EMAIL_REGEX,
          regexHint: EMAIL_REGEX_HELP,
        },
        options: {
          order: 50,
        },
      },
      phone: {
        type: String,
        title: 'Контактный телефон',
        validation: {
          regex: PHONE_REGEX,
          regexHint: PHONE_REGEX_HELP,
        },
        options: {
          order: 60,
        },
      },
      organization: {
        type: Organization,
        title: 'Организация',
        options: {
          order: 70,
          actions: {
            [EntityCommand.CREATE]: false,
            [EntityCommand.UPDATE]: false,
          },
        },
      },
      department: {
        type: String,
        title: 'Структурное подразделение',
        options: {
          order: 80,
        },
      },
      position: {
        type: String,
        title: 'Должность',
        options: {
          order: 90,
        },
      },
      active: {
        type: Boolean,
        title: 'Действует',
        options: {
          order: 100,
        },
      },
      roles: {
        type: RoleDetails,
        title: 'Роли',
        multiple: true,
        readonly: true,
        options: {
          order: 110,
        },
      },
    },
  });
}

export interface UserDTO {
  // Логин
  username: string;
  // Полное имя
  fullName?: string;
  // Имя
  firstName?: string;
  // Отчество
  middleName?: string;
  // Фамилия
  lastName?: string;
  // Электронная почта
  email?: string;
  // Телефон
  phone?: string;
  // Организация
  organization?: Organization;
  // Наименование структурного подразделения
  department?: string;
  // Должность
  position?: string;
  // Статус (Актуальный/неактуальный)
  active?: boolean;
  // Список ролей
  roles?: RoleDetails[];
}
